import React, { useEffect, useState, useRef } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Formik } from "formik";
import { Input } from "components/Input";
import { AutocompleteSelect } from "components/AutocompleteSelect";
import styles from "css_modules/StatsUsers.module.css";

import { request } from 'helpers/tools';
import { ProgressSpinner } from 'components/ProgressSpinner';
import { Button } from 'components/Button';

export const StatsUsers = ({ user }) => {
  const [selectedAccountingCompanyId, setSelectedAccountingCompanyId] = useState(user.company_id);
  const [statsPosted, setStatsPosted] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  // Toggle for hiding inactive users
  const [hideInactive, setHideInactive] = useState(false);

  // Default directly to response_times
  
  const [activeResponseRole, setActiveResponseRole] = useState('all');

  const [modalData, setModalData] = useState({
    isOpen: false,
    title: "",
    type: "",
    userName: "",
    roleName: "",
    docs: []
  });

  const [totals, setTotals] = useState({
    all: { documentsUploaded: 0, documentsAssigned: 0, documentsRemoved: 0, documentsImported: 0 },
    roles: { client: 0, maintenance: 0, manager: 0, accountant: 0, unassigned_role: 0 },
    roles_assigned: { client: 0, maintenance: 0, manager: 0, accountant: 0, unassigned_role: 0 },
    roles_removed: { client: 0, maintenance: 0, manager: 0, accountant: 0, unassigned_role: 0 },
    upload_sources: { web: 0, mobile: 0, email: 0, other: 0 },
    users: {}
  });

  const latestRequestRef = useRef(0);

  const handleYearChange = (option) => { setSelectedYear(option); setStatsPosted(false); };
  const handleMonthChange = (option) => { setSelectedMonth(option); setStatsPosted(false); };
  const handleAccountingCompanySelection = (e) => { setSelectedAccountingCompanyId(e); setStatsPosted(false); };

  const formatHoursToHMin = (hours) => {
    if (hours == null || isNaN(hours) || hours === "") return "-";
    
    const totalMinutes = Math.round(Number(hours) * 60);
    const space = "\u200A";

    if (totalMinutes === 0) return `0${space}min`;

    const d = Math.floor(totalMinutes / (24 * 60));
    const h = Math.floor((totalMinutes % (24 * 60)) / 60);
    const m = totalMinutes % 60;

    const parts = [];
    if (d > 0) parts.push(`${d}${space}d`);
    if (h > 0) parts.push(`${h}${space}h`);
    if (m > 0) parts.push(`${m}${space}min`);

    return parts.length > 0 ? parts.join(" ") : `0${space}min`;
  };

  // Helper to compute average delay excluding documents with 0min delay
  const calcFilteredAvgAndCount = (docs, rawAvg) => {
    if (Array.isArray(docs) && docs.length > 0) {
      const validDocs = docs.filter((d) => {
        if (d.delay_hours == null || isNaN(d.delay_hours)) return false;
        return Math.round(Number(d.delay_hours) * 60) > 0;
      });
      if (validDocs.length > 0) {
        const totalHours = validDocs.reduce((acc, d) => acc + Number(d.delay_hours), 0);
        return { avg: totalHours / validDocs.length, count: validDocs.length };
      }
      return { avg: null, count: 0 };
    }

    if (rawAvg != null && !isNaN(rawAvg) && Math.round(Number(rawAvg) * 60) > 0) {
      return { avg: Number(rawAvg), count: 1 };
    }

    return { avg: null, count: 0 };
  };

  const calculateWeightedAvgDelay = (usersList, delayValKey, countKeyOrFn) => {
    let totalDocs = 0;
    let totalWeightedHours = 0;

    usersList.forEach((u) => {
      const count = typeof countKeyOrFn === 'function' ? countKeyOrFn(u) : (u[countKeyOrFn] || 0);
      const delay = u[delayValKey] || 0;
      if (count > 0 && delay > 0) {
        totalDocs += count;
        totalWeightedHours += delay * count;
      }
    });

    if (totalDocs === 0) return "-";
    return formatHoursToHMin(totalWeightedHours / totalDocs);
  };

  useEffect(() => {
    const getUserStats = async () => {
      const requestId = ++latestRequestRef.current;
      let url = `/get_accounting_company_users_stats/?user=${user.pk}&year=${selectedYear}&month=${selectedMonth}&accounting_company=${selectedAccountingCompanyId}`;

      try {
        const response = await request(url, 'GET');
        if (requestId !== latestRequestRef.current) return;

        if (response && response.ok && response.body && response.body.totals) {
          setTotals(response.body.totals);
          setStatsPosted(true);
        }
      } catch (error) {
        console.error("Chyba při stahování statistik uživatelů:", error);
      }
    };
    getUserStats();
  }, [user.pk, selectedAccountingCompanyId, selectedYear, selectedMonth]);

  const usersArray = Object.keys(totals.users || {}).map((name) => {
    const userData = totals.users[name] || {};
    const currentRoles = userData.roles || {};

    const uploadSources = userData.upload_sources || {};
    const webCount = uploadSources.web || 0;
    const mobileCount = uploadSources.mobile || 0;
    const emailCount = uploadSources.email || 0;

    const crtToReaDocs = (userData.crt_to_rea_docs || []).map(d => ({ ...d, startTime: d.crt_time, endTime: d.rea_time, stageLabel: 'Vytvoření → Příprava' }));
    const reaToAsgDocs = (userData.rea_to_asg_docs || []).map(d => ({ ...d, startTime: d.rea_time, endTime: d.asg_time, stageLabel: 'Příprava → Přiřazení' }));
    const asgToFilDocs = (userData.asg_to_fil_docs || []).map(d => ({ ...d, startTime: d.asg_time, endTime: d.fil_time, stageLabel: 'Přiřazení → Vyplnění' }));
    const filToAsgDocs = (userData.fil_to_asg_docs || []).map(d => ({ ...d, startTime: d.fil_time, endTime: d.asg_time, stageLabel: 'Vyplnění → Přiřazení' }));
    const asgToAprDocs = (userData.asg_to_apr_docs || []).map(d => ({ ...d, startTime: d.asg_time, endTime: d.apr_time, stageLabel: 'Přiřazení → Schválení' }));
    const aprToAsgDocs = (userData.apr_to_asg_docs || []).map(d => ({ ...d, startTime: d.apr_time, endTime: d.asg_time, stageLabel: 'Schválení → Přiřazení' }));
    const asgToImpDocs = (userData.asg_to_imp_docs || []).map(d => ({ ...d, startTime: d.asg_time, endTime: d.imp_time, stageLabel: 'Přiřazení → Zaúčtování' }));

    const rawAvgCrtHours = userData.avg_client_delay_hours ?? userData.avg_crt_delay_hours ?? userData.avg_crt_to_rea_delay_hours;
    const rawAvgMntHours = userData.avg_maintenance_delay_hours ?? userData.avg_rea_delay_hours;
    const rawAvgMntFilHours = userData.avg_maintenance_fil_delay_hours;

    const rawAvgMgtFilHours = userData.avg_manager_fil_delay_hours ?? (userData.fil_to_asg_count ? (userData.total_fil_to_asg_seconds / userData.fil_to_asg_count / 3600) : null);
    const rawAvgMgtAprHours = userData.avg_manager_apr_delay_hours ?? (userData.asg_to_apr_count ? (userData.total_asg_to_apr_seconds / userData.asg_to_apr_count / 3600) : null);

    // Merged doc lists for "Vše"
    const allAssignedDocs = [...reaToAsgDocs, ...filToAsgDocs, ...aprToAsgDocs];
    const allProcessedDocs = [...crtToReaDocs, ...asgToFilDocs, ...asgToAprDocs, ...asgToImpDocs];

    // Calculate averages excluding 0min documents
    const crtRes = calcFilteredAvgAndCount(crtToReaDocs, rawAvgCrtHours);
    const mntRes = calcFilteredAvgAndCount(reaToAsgDocs, rawAvgMntHours);
    const mntFilRes = calcFilteredAvgAndCount(asgToFilDocs, rawAvgMntFilHours);
    const mgtFilRes = calcFilteredAvgAndCount(filToAsgDocs, rawAvgMgtFilHours);
    const mgtAprRes = calcFilteredAvgAndCount(asgToAprDocs, rawAvgMgtAprHours);
    const aprRes = calcFilteredAvgAndCount(aprToAsgDocs, userData.avg_accountant_delay_hours);
    const impRes = calcFilteredAvgAndCount(asgToImpDocs, userData.avg_accountant_imp_delay_hours);

    const allAsgRes = calcFilteredAvgAndCount(allAssignedDocs, null);
    const allPrcRes = calcFilteredAvgAndCount(allProcessedDocs, null);

    const hasClient = userData.clientuser ?? userData.has_client ?? true;
    const hasMaintenance = userData.maintenanceuser ?? userData.has_maintenance ?? false;
    const hasManager = userData.managementuser ?? userData.has_manager ?? false;
    const hasAccountant = userData.accountantuser ?? userData.has_accountant ?? false;

    return {
      name: name,
      id: userData.id,
      totalUploaded: userData.total_uploaded || 0,
      totalAssigned: userData.total_assigned || 0,
      totalRemoved: userData.total_removed || 0,
      totalImported: userData.total_imported ?? asgToImpDocs.length,

      rolesUploaded: userData.roles_uploaded || {},
      rolesRemoved: userData.roles_removed || {},

      web: webCount,
      mobile: mobileCount,
      email: emailCount,

      hasClient,
      hasMaintenance,
      hasManager,
      hasAccountant,

      client: currentRoles.client ?? 0,
      maintenance: currentRoles.maintenance ?? 0,
      manager: currentRoles.manager ?? 0,
      accountant: currentRoles.accountant ?? 0,
      unassigned_role: currentRoles.unassigned_role ?? 0,
      
      avgCrtDelayStr: crtRes.avg != null ? formatHoursToHMin(crtRes.avg) : "-",
      avgMntDelayStr: mntRes.avg != null ? formatHoursToHMin(mntRes.avg) : "-",
      avgMntFilDelayStr: mntFilRes.avg != null ? formatHoursToHMin(mntFilRes.avg) : "-",
      avgMgtFilDelayStr: mgtFilRes.avg != null ? formatHoursToHMin(mgtFilRes.avg) : "-",
      avgMgtAprDelayStr: mgtAprRes.avg != null ? formatHoursToHMin(mgtAprRes.avg) : "-",
      avgAprDelayStr: aprRes.avg != null ? formatHoursToHMin(aprRes.avg) : "-",
      avgImpDelayStr: impRes.avg != null ? formatHoursToHMin(impRes.avg) : "-",

      allAsgDelayStr: allAsgRes.avg != null ? formatHoursToHMin(allAsgRes.avg) : "-",
      allPrcDelayStr: allPrcRes.avg != null ? formatHoursToHMin(allPrcRes.avg) : "-",

      crtDelayVal: crtRes.avg || 0,
      mntDelayVal: mntRes.avg || 0,
      mntFilDelayVal: mntFilRes.avg || 0,
      mgtFilDelayVal: mgtFilRes.avg || 0,
      mgtAprDelayVal: mgtAprRes.avg || 0,
      aprDelayVal: aprRes.avg || 0,
      impDelayVal: impRes.avg || 0,

      allAsgDelayVal: allAsgRes.avg || 0,
      allPrcDelayVal: allPrcRes.avg || 0,

      crtValidCount: crtRes.count,
      mntValidCount: mntRes.count,
      mntFilValidCount: mntFilRes.count,
      mgtFilValidCount: mgtFilRes.count,
      mgtAprValidCount: mgtAprRes.count,
      aprValidCount: aprRes.count,
      impValidCount: impRes.count,

      allAsgValidCount: allAsgRes.count,
      allPrcValidCount: allPrcRes.count,
      
      crtCount: crtToReaDocs.length,
      reaCount: reaToAsgDocs.length,
      mntFilCount: asgToFilDocs.length,
      mgtFilCount: filToAsgDocs.length,
      mgtAprCount: asgToAprDocs.length,
      aprCount: userData.total_assigned || aprToAsgDocs.length,
      impCount: userData.total_imported ?? asgToImpDocs.length,

      allAssignedCount: allAssignedDocs.length,
      allProcessedCount: allProcessedDocs.length,

      crtToReaDocs,
      reaToAsgDocs,
      asgToFilDocs,
      filToAsgDocs,
      asgToAprDocs,
      aprToAsgDocs,
      asgToImpDocs,
      allAssignedDocs,
      allProcessedDocs
    };
  });

  const getRoleUploaded = (u, role) => {
    if (role === 'all') return u.totalUploaded;
    if (u.rolesUploaded && u.rolesUploaded[role] !== undefined) {
      return u.rolesUploaded[role];
    }
    return role === 'client' ? u.totalUploaded : (u[`${role}Uploaded`] ?? 0);
  };

  const getRoleRemoved = (u, role) => {
    if (role === 'all') return u.totalRemoved;
    if (u.rolesRemoved && u.rolesRemoved[role] !== undefined) {
      return u.rolesRemoved[role];
    }
    return role === 'client' ? u.totalRemoved : (u[`${role}Removed`] ?? 0);
  };

  // User is inactive ONLY if all numeric columns in their row equal 0
  const isInactiveUser = (u, role) => {
    const roleUploaded = getRoleUploaded(u, role);
    const roleRemoved = getRoleRemoved(u, role);

    if (roleUploaded > 0 || roleRemoved > 0) return false;

    if (role === 'all') {
      return u.allAssignedCount === 0 && u.allProcessedCount === 0;
    }
    if (role === 'client') {
      return u.crtToReaDocs.length === 0;
    }
    if (role === 'maintenance') {
      return u.reaToAsgDocs.length === 0 && u.asgToFilDocs.length === 0;
    }
    if (role === 'manager') {
      return u.filToAsgDocs.length === 0 && u.asgToAprDocs.length === 0;
    }
    if (role === 'accountant') {
      return u.totalAssigned === 0 && u.totalImported === 0;
    }
    return true;
  };

  const clientUsers = usersArray.filter((u) => u.hasClient && (!hideInactive || !isInactiveUser(u, 'client')));
  const maintenanceUsers = usersArray.filter((u) => u.hasMaintenance && (!hideInactive || !isInactiveUser(u, 'maintenance')));
  const managerUsers = usersArray.filter((u) => u.hasManager && (!hideInactive || !isInactiveUser(u, 'manager')));
  const accountantUsers = usersArray.filter((u) => u.hasAccountant && (!hideInactive || !isInactiveUser(u, 'accountant')));
  const allUsers = usersArray.filter((u) => !hideInactive || !isInactiveUser(u, 'all'));

  const openDocModal = (type, userName, docs, roleName = "") => {
    if (!docs || docs.length === 0) return;
    
    let title = "";
    if (type === 'crt_rea') {
      title = "Detail odezvy klienta: Vytvoření → Příprava";
    } else if (type === 'rea_asg') {
      title = "Detail odezvy obsluhy: Příprava → Přiřazení";
    } else if (type === 'asg_fil') {
      title = "Detail odezvy obsluhy: Přiřazení → Vyplnění";
    } else if (type === 'fil_asg') {
      title = "Detail odezvy manažera: Vyplnění → Přiřazení";
    } else if (type === 'asg_apr') {
      title = "Detail odezvy manažera: Přiřazení → Schválení";
    } else if (type === 'apr_asg') {
      title = "Detail odezvy účetního: Schválení → Přiřazení";
    } else if (type === 'asg_imp') {
      title = "Detail odezvy účetního: Přiřazení → Zaúčtování";
    } else if (type === 'all_asg') {
      title = "Detail odezvy: Přiřazené doklady (všechny role)";
    } else if (type === 'all_prc') {
      title = "Detail odezvy: Zpracované doklady (všechny role)";
    }

    setModalData({ isOpen: true, title, type, userName, roleName, docs });
  };

  const closeModal = () => {
    setModalData({ isOpen: false, title: "", type: "", userName: "", roleName: "", docs: [] });
  };

  const formatDateTime = (isoStr) => {
    if (!isoStr) return "-";
    const date = new Date(isoStr);
    return date.toLocaleString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const accountingCompaniesWithIds = [
    ...(user.master_docspoint_accounting_companies || []).map((company) => ({ id: company.id, title: company.title })),
  ];

  const currentYr = new Date().getFullYear();
  const years = Array.from({ length: 3 }, (_, i) => ({ title: `${currentYr - i}`, id: currentYr - i }));
  const months = [
    { title: 'Leden', id: 1 }, { title: 'Únor', id: 2 }, { title: 'Březen', id: 3 },
    { title: 'Duben', id: 4 }, { title: 'Květen', id: 5 }, { title: 'Červen', id: 6 },
    { title: 'Červenec', id: 7 }, { title: 'Srpen', id: 8 }, { title: 'Září', id: 9 },
    { title: 'Říjen', id: 10 }, { title: 'Listopad', id: 11 }, { title: 'Prosinec', id: 12 },
  ];

  const compactCell = { padding: '0.4rem 0.75rem', lineHeight: '1.4' };

  return (
    <div style={{ backgroundColor: 'white', overflowY: "auto", height: "100vh", width:"100%" }}>
      <div style={{ background: 'var(--semilight)', padding: "1rem 1rem 0.5rem 1rem" }}>
        <h1 style={{ background: 'var(--semilight)', fontWeight: 700, color: 'var(--purple)', fontSize: 'var(--xl)', margin: 0 }}>
          Statistiky aktivity uživatelů
        </h1>
      </div>

      {/* TOP CONTROLS ROW */}
      <div style={{ background: 'var(--semilight)', padding: "0.5rem 1rem 1rem 1rem" }}>
        <Formik initialValues={{}}>
          {() => (
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: '1.25rem', flexWrap: 'nowrap', width: '100%' }}>
              
              {/* Rok */}
              <div style={{ width: '130px', flexShrink: 0 }}>
                <Input name="year" component={AutocompleteSelect} data={years} selectedValue={selectedYear} onChange={handleYearChange} className={styles.input} noErrorPlaceholder noBlankOption>Rok</Input>
              </div>

              {/* Měsíc */}
              <div style={{ width: '160px', flexShrink: 0 }}>
                <Input name="month" component={AutocompleteSelect} data={months} selectedValue={selectedMonth} onChange={handleMonthChange} className={styles.input} noErrorPlaceholder noBlankOption>Měsíc</Input>
              </div>
              
              {/* Účetní společnost */}
              {user.master_docspoint_admin === true && (
                <div style={{ width: '280px', flexShrink: 0 }}>
                  <Input name="accounting_companies" component={AutocompleteSelect} data={accountingCompaniesWithIds} selectedValue={selectedAccountingCompanyId} onChange={handleAccountingCompanySelection} className={styles.input} noBlankOption noErrorPlaceholder>
                    Účetní společnost
                  </Input>
                </div>
              )}

              {/* Skrýt nečinné uživatele */}
              <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', height: '38px', paddingBottom: '2px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem', color: '#1e293b', userSelect: 'none', whiteSpace: 'nowrap', margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={hideInactive}
                    onChange={(e) => setHideInactive(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--purple)' }}
                  />
                  Skrýt nečinné uživatele
                </label>
              </div>

            </div>
          )}
        </Formik>
      </div>

      <div className={styles.container}>
        {statsPosted === false && <ProgressSpinner />}

        {statsPosted === true && (
          <div>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
              <button
                style={{
                  padding: '0.4rem 1rem',
                  backgroundColor: activeResponseRole === 'all' ? 'var(--purple)' : 'var(--semilight)',
                  color: activeResponseRole === 'all' ? 'white' : 'var(--black)',
                  border: '1px solid ' + (activeResponseRole === 'all' ? 'var(--purple)' : 'var(--light)'),
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
                onClick={() => setActiveResponseRole('all')}
              >
                Vše
              </button>
              <button
                style={{
                  padding: '0.4rem 1rem',
                  backgroundColor: activeResponseRole === 'client' ? 'var(--purple)' : 'var(--semilight)',
                  color: activeResponseRole === 'client' ? 'white' : 'var(--black)',
                  border: '1px solid ' + (activeResponseRole === 'client' ? 'var(--purple)' : 'var(--light)'),
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
                onClick={() => setActiveResponseRole('client')}
              >
               Základní uživatelé
              </button>
              <button
                style={{
                  padding: '0.4rem 1rem',
                  backgroundColor: activeResponseRole === 'maintenance' ? 'var(--purple)' : 'var(--semilight)',
                  color: activeResponseRole === 'maintenance' ? 'white' : 'var(--black)',
                  border: '1px solid ' + (activeResponseRole === 'maintenance' ? 'var(--purple)' : 'var(--light)'),
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
                onClick={() => setActiveResponseRole('maintenance')}
              >
               Obslužní uživatelé
              </button>
              <button
                style={{
                  padding: '0.4rem 1rem',
                  backgroundColor: activeResponseRole === 'manager' ? 'var(--purple)' : 'var(--semilight)',
                  color: activeResponseRole === 'manager' ? 'white' : 'var(--black)',
                  border: '0px solid ' + (activeResponseRole === 'manager' ? 'var(--purple)' : 'var(--light)'),
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
                onClick={() => setActiveResponseRole('manager')}
              >
                 Schvalovací uživatelé
              </button>
              <button
                style={{
                  padding: '0.4rem 1rem',
                  backgroundColor: activeResponseRole === 'accountant' ? 'var(--purple)' : 'var(--semilight)',
                  color: activeResponseRole === 'accountant' ? 'white' : 'var(--black)',
                  border: '0px solid ' + (activeResponseRole === 'accountant' ? 'var(--primarylight)' : 'var(--dark)'),
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
                onClick={() => setActiveResponseRole('accountant')}
              >
                Účetní uživatelé
              </button>
              
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "50vw 30vw", gap: "2rem", width: "100%" }}>
              
              <div>
                {activeResponseRole === 'client' && (
                  <div>
                    <table className={styles.statsTable}>
                      <tbody>
                        <tr className={styles.stickyHeader}>
                          <td className={styles.thCc}>Jméno uživatele ({clientUsers.length})</td>
                          <td className={styles.thStd}>Nahráno</td>
                          <td className={styles.thStd}>Odstraněno</td>
                          <td className={styles.thStd}>Zpracované doklady</td>
                          <td className={styles.thTimeMnt}>Průměrná doba zpracování</td>
                        </tr>

                        {clientUsers.length === 0 ? (
                          <tr>
                            <td colSpan="5" className={styles.tdBody} style={{ textAlign: "center", color: "#64748b" }}>
                              Žádní základní uživatelé
                            </td>
                          </tr>
                        ) : (
                          clientUsers.map((u, idx) => {
                            const hasCrt = u.crtToReaDocs.length > 0;

                            return (
                              <tr key={idx} className={styles.dataRow}>
                                <td className={styles.tdBodyLeft} title={u.name}>{u.name}</td>
                                <td className={styles.tdBody}>{getRoleUploaded(u, 'client')}</td>
                                <td className={styles.tdBody}>{getRoleRemoved(u, 'client')}</td>
                                <td className={styles.tdBody}>{u.crtToReaDocs.length}</td>

                                <td 
                                  className={hasCrt ? styles.clickableCellMnt : styles.disabledCell}
                                  onClick={() => openDocModal('crt_rea', u.name, u.crtToReaDocs, 'Základní uživatelé')}
                                  title={hasCrt ? "Klikněte pro detail dokumentů" : ""}
                                >
                                  {u.avgCrtDelayStr}
                                </td>
                              </tr>
                            );
                          })
                        )}

                        <tr className={styles.footRow}>
                          <td className={styles.tdBodyLeft} style={{ fontWeight: "bold" }}>Celkem</td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {clientUsers.reduce((acc, u) => acc + getRoleUploaded(u, 'client'), 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {clientUsers.reduce((acc, u) => acc + getRoleRemoved(u, 'client'), 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {clientUsers.reduce((acc, u) => acc + u.crtToReaDocs.length, 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {calculateWeightedAvgDelay(clientUsers, 'crtDelayVal', 'crtValidCount')}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {activeResponseRole === 'maintenance' && (
                  <div>
                   <table className={styles.statsTable}>
                      <tbody>
                        <tr className={styles.stickyHeader}>
                          <td className={styles.thCc}>Jméno uživatele ({maintenanceUsers.length})</td>
                          <td className={styles.thStd}>Nahráno</td>
                          <td className={styles.thStd}>Odstraněno</td>
                          <td className={styles.thStd}>Přiřazené doklady</td>
                          <td className={styles.thTimeMnt}>Průměrná doba přiřazení</td>
                          <td className={styles.thStd}>Zpracované doklady</td>
                          <td className={styles.thTimeMnt}>Průměrná doba zpracování</td>
                        </tr>

                        {maintenanceUsers.length === 0 ? (
                          <tr>
                            <td colSpan="7" className={styles.tdBody} style={{ textAlign: "center", color: "#64748b" }}>
                              Žádní obslužní uživatelé
                            </td>
                          </tr>
                        ) : (
                          maintenanceUsers.map((u, idx) => {
                            const hasRea = u.reaToAsgDocs.length > 0;
                            const hasFil = u.asgToFilDocs.length > 0;

                            return (
                              <tr key={idx} className={styles.dataRow}>
                                <td className={styles.tdBodyLeft} title={u.name}>{u.name}</td>
                                <td className={styles.tdBody}>{getRoleUploaded(u, 'maintenance')}</td>
                                <td className={styles.tdBody}>{getRoleRemoved(u, 'maintenance')}</td>
                                <td className={styles.tdBody}>{u.reaToAsgDocs.length}</td>

                                <td 
                                  className={hasRea ? styles.clickableCellMnt : styles.disabledCell}
                                  onClick={() => openDocModal('rea_asg', u.name, u.reaToAsgDocs, 'Obslužní uživatelé')}
                                  title={hasRea ? "Klikněte pro detail dokumentů" : ""}
                                >
                                  {u.avgMntDelayStr}
                                </td>

                                <td className={styles.tdBody}>{u.asgToFilDocs.length}</td>

                                <td 
                                  className={hasFil ? styles.clickableCellMnt : styles.disabledCell}
                                  onClick={() => openDocModal('asg_fil', u.name, u.asgToFilDocs, 'Obslužní uživatelé')}
                                  title={hasFil ? "Klikněte pro detail dokumentů" : ""}
                                >
                                  {u.avgMntFilDelayStr}
                                </td>
                              </tr>
                            );
                          })
                        )}

                        <tr className={styles.footRow}>
                          <td className={styles.tdBodyLeft} style={{ fontWeight: "bold" }}>Celkem</td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {maintenanceUsers.reduce((acc, u) => acc + getRoleUploaded(u, 'maintenance'), 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {maintenanceUsers.reduce((acc, u) => acc + getRoleRemoved(u, 'maintenance'), 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {maintenanceUsers.reduce((acc, u) => acc + u.reaToAsgDocs.length, 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {calculateWeightedAvgDelay(maintenanceUsers, 'mntDelayVal', 'mntValidCount')}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {maintenanceUsers.reduce((acc, u) => acc + u.asgToFilDocs.length, 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {calculateWeightedAvgDelay(maintenanceUsers, 'mntFilDelayVal', 'mntFilValidCount')}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {activeResponseRole === 'manager' && (
                  <div>
                    <table className={styles.statsTable}>
                      <tbody>
                        <tr className={styles.stickyHeader}>
                          <td className={styles.thCc}>Jméno uživatele ({managerUsers.length})</td>
                          <td className={styles.thStd}>Nahráno</td>
                          <td className={styles.thStd}>Odstraněno</td>
                          <td className={styles.thStd}>Přiřazené doklady</td>
                          <td className={styles.thTimeMnt}>Průměrná doba přiřazení</td>
                          <td className={styles.thStd}>Zpracované doklady</td>
                          <td className={styles.thTimeMnt}>Průměrná doba zpracování</td>
                        </tr>

                        {managerUsers.length === 0 ? (
                          <tr>
                            <td colSpan="7" className={styles.tdBody} style={{ textAlign: "center", color: "#64748b" }}>
                              Žádní schvalovací uživatelé
                            </td>
                          </tr>
                        ) : (
                          managerUsers.map((u, idx) => {
                            const hasFilAsg = u.filToAsgDocs.length > 0;
                            const hasAsgApr = u.asgToAprDocs.length > 0;

                            return (
                              <tr key={idx} className={styles.dataRow}>
                                <td className={styles.tdBodyLeft} title={u.name}>{u.name}</td>
                                <td className={styles.tdBody}>{getRoleUploaded(u, 'manager')}</td>
                                <td className={styles.tdBody}>{getRoleRemoved(u, 'manager')}</td>
                                <td className={styles.tdBody}>{u.filToAsgDocs.length}</td>

                                <td 
                                  className={hasFilAsg ? styles.clickableCellMnt : styles.disabledCell}
                                  onClick={() => openDocModal('fil_asg', u.name, u.filToAsgDocs, 'Schvalovací uživatelé')}
                                  title={hasFilAsg ? "Klikněte pro detail dokumentů" : ""}
                                >
                                  {u.avgMgtFilDelayStr}
                                </td>

                                <td className={styles.tdBody}>{u.asgToAprDocs.length}</td>

                                <td 
                                  className={hasAsgApr ? styles.clickableCellMnt : styles.disabledCell}
                                  onClick={() => openDocModal('asg_apr', u.name, u.asgToAprDocs, 'Schvalovací uživatelé')}
                                  title={hasAsgApr ? "Klikněte pro detail dokumentů" : ""}
                                >
                                  {u.avgMgtAprDelayStr}
                                </td>
                              </tr>
                            );
                          })
                        )}

                        <tr className={styles.footRow}>
                          <td className={styles.tdBodyLeft} style={{ fontWeight: "bold" }}>Celkem</td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {managerUsers.reduce((acc, u) => acc + getRoleUploaded(u, 'manager'), 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {managerUsers.reduce((acc, u) => acc + getRoleRemoved(u, 'manager'), 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {managerUsers.reduce((acc, u) => acc + u.filToAsgDocs.length, 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {calculateWeightedAvgDelay(managerUsers, 'mgtFilDelayVal', 'mgtFilValidCount')}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {managerUsers.reduce((acc, u) => acc + u.asgToAprDocs.length, 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {calculateWeightedAvgDelay(managerUsers, 'mgtAprDelayVal', 'mgtAprValidCount')}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {activeResponseRole === 'accountant' && (
                  <div>
                    <table className={styles.statsTable}>
                      <tbody>
                        <tr className={styles.stickyHeader}>
                          <td className={styles.thCc}>Jméno uživatele ({accountantUsers.length})</td>
                          <td className={styles.thStd}>Nahráno</td>
                          <td className={styles.thStd}>Odstraněno</td>
                          <td className={styles.thStd}>Přiřazené doklady</td>
                          <td className={styles.thTime}>Průměrná doba přiřazení</td>
                          <td className={styles.thStd}>Zpracované doklady</td>
                          <td className={styles.thTime}>Průměrná doba zpracování</td>
                        </tr>

                        {accountantUsers.length === 0 ? (
                          <tr>
                            <td colSpan="7" className={styles.tdBody} style={{ textAlign: "center", color: "#64748b" }}>
                              Žádní účetní uživatelé
                            </td>
                          </tr>
                        ) : (
                          accountantUsers.map((u, idx) => {
                            const hasApr = u.aprToAsgDocs.length > 0;
                            const hasImp = u.asgToImpDocs.length > 0;

                            return (
                              <tr key={idx} className={styles.dataRow}>
                                <td className={styles.tdBodyLeft} title={u.name}>{u.name}</td>
                                <td className={styles.tdBody}>{getRoleUploaded(u, 'accountant')}</td>
                                <td className={styles.tdBody}>{getRoleRemoved(u, 'accountant')}</td>
                                <td className={styles.tdBody}>{u.totalAssigned}</td>
                                
                                <td 
                                  className={hasApr ? styles.clickableCellApr : styles.disabledCell}
                                  onClick={() => openDocModal('apr_asg', u.name, u.aprToAsgDocs, 'Účetní uživatelé')}
                                  title={hasApr ? "Klikněte pro detail dokumentů" : ""}
                                >
                                  {u.avgAprDelayStr}
                                </td>

                                <td className={styles.tdBody}>{u.totalImported}</td>

                                <td 
                                  className={hasImp ? styles.clickableCellImp : styles.disabledCell}
                                  onClick={() => openDocModal('asg_imp', u.name, u.asgToImpDocs, 'Účetní uživatelé')}
                                  title={hasImp ? "Klikněte pro detail dokumentů" : ""}
                                >
                                  {u.avgImpDelayStr}
                                </td>
                              </tr>
                            );
                          })
                        )}

                        <tr className={styles.footRow}>
                          <td className={styles.tdBodyLeft} style={{ fontWeight: "bold" }}>Celkem</td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {accountantUsers.reduce((acc, u) => acc + getRoleUploaded(u, 'accountant'), 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {accountantUsers.reduce((acc, u) => acc + getRoleRemoved(u, 'accountant'), 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {accountantUsers.reduce((acc, u) => acc + u.totalAssigned, 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {calculateWeightedAvgDelay(accountantUsers, 'aprDelayVal', 'aprValidCount')}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {accountantUsers.reduce((acc, u) => acc + u.totalImported, 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {calculateWeightedAvgDelay(accountantUsers, 'impDelayVal', 'impValidCount')}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {activeResponseRole === 'all' && (
                  <div>
                    <table className={styles.statsTable}>
                      <tbody>
                        <tr className={styles.stickyHeader}>
                          <td className={styles.thCc}>Jméno uživatele ({allUsers.length})</td>
                          <td className={styles.thStd}>Nahráno</td>
                          <td className={styles.thStd}>Odstraněno</td>
                          <td className={styles.thStd}>Přiřazené doklady</td>
                          <td className={styles.thTimeMnt}>Průměrná doba přiřazení</td>
                          <td className={styles.thStd}>Zpracované doklady</td>
                          <td className={styles.thTimeMnt}>Průměrná doba zpracování</td>
                        </tr>

                        {allUsers.length === 0 ? (
                          <tr>
                            <td colSpan="7" className={styles.tdBody} style={{ textAlign: "center", color: "#64748b" }}>
                              Žádní uživatelé
                            </td>
                          </tr>
                        ) : (
                          allUsers.map((u, idx) => {
                            const hasAllAsg = u.allAssignedDocs.length > 0;
                            const hasAllPrc = u.allProcessedDocs.length > 0;

                            return (
                              <tr key={idx} className={styles.dataRow}>
                                <td className={styles.tdBodyLeft} title={u.name}>{u.name}</td>
                                <td className={styles.tdBody}>{u.totalUploaded}</td>
                                <td className={styles.tdBody}>{u.totalRemoved}</td>
                                <td className={styles.tdBody}>{u.allAssignedCount}</td>

                                <td 
                                  className={hasAllAsg ? styles.clickableCellMnt : styles.disabledCell}
                                  onClick={() => openDocModal('all_asg', u.name, u.allAssignedDocs, 'Všechny role')}
                                  title={hasAllAsg ? "Klikněte pro detail dokumentů" : ""}
                                >
                                  {u.allAsgDelayStr}
                                </td>

                                <td className={styles.tdBody}>{u.allProcessedCount}</td>

                                <td 
                                  className={hasAllPrc ? styles.clickableCellMnt : styles.disabledCell}
                                  onClick={() => openDocModal('all_prc', u.name, u.allProcessedDocs, 'Všechny role')}
                                  title={hasAllPrc ? "Klikněte pro detail dokumentů" : ""}
                                >
                                  {u.allPrcDelayStr}
                                </td>
                              </tr>
                            );
                          })
                        )}

                        <tr className={styles.footRow}>
                          <td className={styles.tdBodyLeft} style={{ fontWeight: "bold" }}>Celkem</td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {allUsers.reduce((acc, u) => acc + u.totalUploaded, 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {allUsers.reduce((acc, u) => acc + u.totalRemoved, 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {allUsers.reduce((acc, u) => acc + u.allAssignedCount, 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {calculateWeightedAvgDelay(allUsers, 'allAsgDelayVal', 'allAsgValidCount')}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {allUsers.reduce((acc, u) => acc + u.allProcessedCount, 0)}
                          </td>
                          <td className={styles.tdBody} style={{ fontWeight: "bold" }}>
                            {calculateWeightedAvgDelay(allUsers, 'allPrcDelayVal', 'allPrcValidCount')}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div style={{ marginTop: "0rem", paddingRight: "2rem" }}>
                <div style={{ width: '100%', height: '15rem', marginBottom: '3rem', marginTop: "-4rem" }}>
                  <h2 style={{ fontWeight: "500" }}>Průměrné časy (hodiny)</h2>
                  {activeResponseRole === 'client' && (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={clientUsers} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} unit=" h" />
                        <Tooltip formatter={(value, name) => [formatHoursToHMin(value), name]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="crtDelayVal" name="Příprava (h)" fill="#2563eb" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}

                  {activeResponseRole === 'maintenance' && (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={maintenanceUsers} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} unit=" h" />
                        <Tooltip formatter={(value, name) => [formatHoursToHMin(value), name]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="mntDelayVal" name="Přiřazení (h)" fill="#059669" />
                        <Bar dataKey="mntFilDelayVal" name="Vyplnění (h)" fill="#0d9488" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}

                  {activeResponseRole === 'manager' && (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={managerUsers} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} unit=" h" />
                        <Tooltip formatter={(value, name) => [formatHoursToHMin(value), name]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="mgtFilDelayVal" name="Přiřazení (h)" fill="#d97706" />
                        <Bar dataKey="mgtAprDelayVal" name="Schválení (h)" fill="#b45309" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}

                  {activeResponseRole === 'accountant' && (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={accountantUsers} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} unit=" h" />
                        <Tooltip formatter={(value, name) => [formatHoursToHMin(value), name]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="aprDelayVal" name="Přiřazení (h)" fill="#4f46e5" />
                        <Bar dataKey="impDelayVal" name="Zaúčtování (h)" fill="#0284c7" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}

                  {activeResponseRole === 'all' && (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={allUsers} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} unit=" h" />
                        <Tooltip formatter={(value, name) => [formatHoursToHMin(value), name]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="allAsgDelayVal" name="Přiřazení (h)" fill="#8b5cf6" />
                        <Bar dataKey="allPrcDelayVal" name="Zpracování (h)" fill="#6366f1" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div style={{ width: '100%', height: '15rem' }}>
                  <h2 style={{ fontWeight: "500" }}>Počet zpracovaných dokumentů</h2>
                  {activeResponseRole === 'client' && (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={clientUsers} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip formatter={(value, name) => [value, name]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="crtCount" name="Příprava" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}

                  {activeResponseRole === 'maintenance' && (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={maintenanceUsers} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip formatter={(value, name) => [value, name]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="reaCount" name="Přiřazené doklady" fill="#10b981" />
                        <Bar dataKey="mntFilCount" name="Zpracované doklady" fill="#14b8a6" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}

                  {activeResponseRole === 'manager' && (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={managerUsers} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip formatter={(value, name) => [value, name]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="mgtFilCount" name="Přiřazené doklady" fill="#f59e0b" />
                        <Bar dataKey="mgtAprCount" name="Zpracované doklady" fill="#d97706" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}

                  {activeResponseRole === 'accountant' && (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={accountantUsers} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip formatter={(value, name) => [value, name]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="aprCount" name="Přiřazeno" fill="#6366f1" />
                        <Bar dataKey="impCount" name="Zaúčtováno" fill="#0284c7" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}

                  {activeResponseRole === 'all' && (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={allUsers} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip formatter={(value, name) => [value, name]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="allAssignedCount" name="Přiřazené doklady" fill="#8b5cf6" />
                        <Bar dataKey="allProcessedCount" name="Zpracované doklady" fill="#6366f1" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

            </div>
          </div>
        )}

        {modalData.isOpen && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalCard}>
              
              <div className={styles.modalHeader}>
                <div>
                  <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.1rem', fontWeight: 'bold' }}>{modalData.title}</h3>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                    {modalData.roleName ? `${modalData.roleName}: ` : "Uživatel: "}
                    <strong>{modalData.userName}</strong> ({modalData.docs.length} dokumentů)
                  </span>
                </div>
                <button onClick={closeModal} className={styles.modalCloseBtn}>
                  ✕
                </button>
              </div>

              <div className={styles.modalBody}>
                <table className={styles.modalTable}>
                  <thead>
                    <tr className={styles.stickyHeader}>
                      <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'left' }}>Document UUID</td>
                      {(modalData.type === 'all_asg' || modalData.type === 'all_prc') && (
                        <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'left' }}>Fáze / Krok</td>
                      )}
                      {modalData.type === 'crt_rea' && (
                        <>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Vytvoření</td>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Přípravy</td>
                        </>
                      )}
                      {modalData.type === 'rea_asg' && (
                        <>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Přípravy</td>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Přiřazení</td>
                        </>
                      )}
                      {modalData.type === 'asg_fil' && (
                        <>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Přiřazení</td>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Vyplnění</td>
                        </>
                      )}
                      {modalData.type === 'fil_asg' && (
                        <>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Vyplnění</td>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Přiřazení</td>
                        </>
                      )}
                      {modalData.type === 'asg_apr' && (
                        <>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Přiřazení</td>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Schválení</td>
                        </>
                      )}
                      {modalData.type === 'apr_asg' && (
                        <>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Schválení</td>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Přiřazení</td>
                        </>
                      )}
                      {modalData.type === 'asg_imp' && (
                        <>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Přiřazení</td>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Zaúčtování</td>
                        </>
                      )}
                      {(modalData.type === 'all_asg' || modalData.type === 'all_prc') && (
                        <>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Začátku</td>
                          <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'center' }}>Čas Konce</td>
                        </>
                      )}
                      <td className={styles.modalTh} style={{ ...compactCell, textAlign: 'right' }}>Doba trvání</td>
                    </tr>
                  </thead>
                  <tbody>
                    {modalData.docs.map((doc, idx) => (
                      <tr key={idx} className={styles.dataRowPopup}>
                        <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'left', fontFamily: 'monospace', color: 'var(--black)' }}>{doc.doc_uuid}</td>
                        
                        {(modalData.type === 'all_asg' || modalData.type === 'all_prc') && (
                          <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'left', fontSize: '0.8rem', color: '#475569' }}>{doc.stageLabel || "-"}</td>
                        )}

                        {modalData.type === 'crt_rea' && (
                          <>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.crt_time)}</td>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.rea_time)}</td>
                          </>
                        )}
                        {modalData.type === 'rea_asg' && (
                          <>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.rea_time)}</td>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.asg_time)}</td>
                          </>
                        )}
                        {modalData.type === 'asg_fil' && (
                          <>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.asg_time)}</td>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.fil_time)}</td>
                          </>
                        )}
                        {modalData.type === 'fil_asg' && (
                          <>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.fil_time)}</td>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.asg_time)}</td>
                          </>
                        )}
                        {modalData.type === 'asg_apr' && (
                          <>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.asg_time)}</td>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.apr_time)}</td>
                          </>
                        )}
                        {modalData.type === 'apr_asg' && (
                          <>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.apr_time)}</td>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.asg_time)}</td>
                          </>
                        )}
                        {modalData.type === 'asg_imp' && (
                          <>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.asg_time)}</td>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.imp_time)}</td>
                          </>
                        )}
                        {(modalData.type === 'all_asg' || modalData.type === 'all_prc') && (
                          <>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.startTime)}</td>
                            <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'center' }}>{formatDateTime(doc.endTime)}</td>
                          </>
                        )}
                        
                        <td className={styles.modalTd} style={{ ...compactCell, textAlign: 'right', fontWeight: 'bold', color: '#0284c7' }}>
                          {formatHoursToHMin(doc.delay_hours)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={styles.modalFooter}>
                <Button onClick={closeModal}>Zavřít</Button>
              </div>

            </div>
          </div>
        )}

      </div>
    </div>
  );
};